import { UltrarubigramStore, UltrarubigramStoreType } from './UltrarubigramStore';
import { PuzzleNames, STATS_DIALOG_DELAY, UltrarubigramData } from '../../../utils/types';
import { savePuzzleProgress } from '../../../utils/persistenceUtils';
import {
  Edge, GridState, MoveKind, PiecePath, QuadrantState, SubgridState,
  GRID_HALF_SIZE, PUZZLE_HALF_SIZE, QUADRANT_HALF_SIZE, SUBGRID_HALF_SIZE,
  apply, cellOffset, childOffset, compose, fillContent, flipPieceOnScreen, gridsMatch, inverse,
  isMirrored, makeEmptyGrids, movesTo, orientationElement, parentElement, pieceAt,
  renderScreenGrid, rotatePieceOnScreen, rotationElement, savedOrientations, screenElement,
  solvePuzzle,
} from './geometry';

/** Milliseconds a flip takes; matches the CSS transition. */
export const FLIP_DURATION = 600;
/** How long letters stay in the pre-flip orientation before snapping, while the piece is edge-on. */
const FLIP_HIDE_DURATION = 300;

export const initUltrarubigram = (puzzleData: UltrarubigramData) => {
  UltrarubigramStore.update(s => {
    s.puzzleId = puzzleData.puzzleId;
    s.solution = puzzleData.grid.map(row => row.split(''));
    s.grids = makeEmptyGrids();
    fillContent(s.grids, puzzleData.scrambledGrid.map(row => row.split('')));
    s.rot = 0;
    s.isRevealing = false;
    s.isCelebrating = false;
  });
};

export const onSolve = () => {
  const { time, puzzleId } = UltrarubigramStore.getRawState();

  playVictoryAnimation(() => {
    setTimeout(() => {
      UltrarubigramStore.update(s => {
        s.statsDialogIsOpen = true;
      });
    }, STATS_DIALOG_DELAY_AFTER_VICTORY);
  });

  savePuzzleProgress(PuzzleNames.Ultrarubigram, {
    puzzleId,
    revealed: false,
    solved: true,
    time,
    // a finished puzzle needs no orientations, and this drops any earlier mid-puzzle save
    partialState: null,
  });
};

export const onReveal = () => {
  const { time, puzzleId } = UltrarubigramStore.getRawState();

  UltrarubigramStore.update(s => {
    clearInterval(s.timerUpdatingFn);
    s.revealed = true;
    s.isPaused = false;
    s.isRevealing = true;
  });

  savePuzzleProgress(PuzzleNames.Ultrarubigram, {
    puzzleId,
    revealed: true,
    solved: false,
    time,
    partialState: null,
  });

  playRevealAnimation();
};

/** On pause or navigating away: the time and where every piece is. */
export const saveUltrarubigram = () => {
  const { solved, revealed, time, rot, grids, puzzleId } = UltrarubigramStore.getRawState();

  if (solved || revealed) return;

  savePuzzleProgress(PuzzleNames.Ultrarubigram, {
    puzzleId,
    revealed,
    solved,
    time,
    partialState: {
      rot,
      grids: savedOrientations(grids),
    },
  });
};

// ---------------------------------------------------------------------------
// Moves
// ---------------------------------------------------------------------------

/**
 * Marks a piece as moving for as long as its animation runs: it (and whatever it sits inside)
 * is then raised above its neighbours, so it passes over them (see .moving).
 */
const beginMove = (s: UltrarubigramStoreType, path: PiecePath, kind: MoveKind) => {
  const piece = pieceAt(s, path);
  piece.moving = kind;
  const move = ++piece.moves;
  setTimeout(() => {
    UltrarubigramStore.update(_s => {
      const later = pieceAt(_s, path);
      if (later.moves === move) later.moving = null;
    });
  }, FLIP_DURATION);
};

const checkSolved = (s: UltrarubigramStoreType) => {
  if (s.solved || s.revealed) return;
  if (gridsMatch(renderScreenGrid(s.rot, s.grids), s.solution)) {
    s.solved = true;
    onSolve();
  }
};

/** Marks a piece as mid-flip for the moment its letters must stay put, then clears it. */
const startFlipping = (s: UltrarubigramStoreType, path: PiecePath) => {
  pieceAt(s, path).isFlipping = true;
  beginMove(s, path, 'flip');

  setTimeout(() => {
    UltrarubigramStore.update(_s => {
      pieceAt(_s, path).isFlipping = false;
    });
  }, FLIP_HIDE_DURATION);
};

const toggleFlip = (s: UltrarubigramStoreType, path: PiecePath, toggle: 'H' | 'V') => {
  const piece = pieceAt(s, path);
  if (toggle === 'H') piece.flipH = !piece.flipH;
  else piece.flipV = !piece.flipV;
  startFlipping(s, path);
};

const flipOnScreen = (s: UltrarubigramStoreType, path: PiecePath, horizontal: boolean) => {
  flipPieceOnScreen(s, path, horizontal);
  startFlipping(s, path);
};

const rotateOnScreen = (s: UltrarubigramStoreType, path: PiecePath, clockwise: boolean) => {
  rotatePieceOnScreen(s, path, clockwise);
  beginMove(s, path, 'turn');
};

const interactive = (s: UltrarubigramStoreType) => !s.solved && !s.revealed && !s.isRevealing;

export const rotatePuzzle = (clockwise: boolean) => {
  UltrarubigramStore.update(s => {
    if (!interactive(s)) return;
    s.rot += clockwise ? 90 : -90;
    checkSolved(s);
  });
};

/**
 * The outer arrow buttons turn with the puzzle, so the axis each one shows is the puzzle's own
 * and the grid's local flip is the one to toggle.
 */
export const flipGrid = (grid: number, toggle: 'H' | 'V') => {
  UltrarubigramStore.update(s => {
    if (!interactive(s)) return;
    toggleFlip(s, { grid }, toggle);
    checkSolved(s);
  });
};

export const rotateGrid = (grid: number, clockwise: boolean) => {
  UltrarubigramStore.update(s => {
    if (!interactive(s)) return;
    rotateOnScreen(s, { grid }, clockwise);
    checkSolved(s);
  });
};

/**
 * The small tabs on a quadrant's edges flip it across the axis through that edge's midpoint;
 * `edge` is the edge as seen on screen, so a tab on the left or right swaps left and right.
 */
export const flipQuadrantByEdge = (grid: number, quadrant: number, edge: Edge) => {
  UltrarubigramStore.update(s => {
    if (!interactive(s)) return;
    flipOnScreen(s, { grid, quadrant }, edge === 'left' || edge === 'right');
    checkSolved(s);
  });
};

export const rotateQuadrant = (grid: number, quadrant: number, clockwise: boolean) => {
  UltrarubigramStore.update(s => {
    if (!interactive(s)) return;
    rotateOnScreen(s, { grid, quadrant }, clockwise);
    checkSolved(s);
  });
};

/** A subgrid's side cells (not corners, not center) flip it across the axis through that cell. */
export const flipSubgridByCell = (grid: number, quadrant: number, subgrid: number, row: number, col: number) => {
  UltrarubigramStore.update(s => {
    if (!interactive(s)) return;
    const path = { grid, quadrant, subgrid };
    const [x] = apply(screenElement(s, path), ...cellOffset(row, col));
    flipOnScreen(s, path, x !== 0);
    checkSolved(s);
  });
};

export const rotateSubgrid = (grid: number, quadrant: number, subgrid: number, clockwise: boolean) => {
  UltrarubigramStore.update(s => {
    if (!interactive(s)) return;
    rotateOnScreen(s, { grid, quadrant, subgrid }, clockwise);
    checkSolved(s);
  });
};

// ---------------------------------------------------------------------------
// Reveal: play the puzzle to its solution
// ---------------------------------------------------------------------------

type Move = { path: PiecePath; toggle: 'H' | 'V' | null; deltaRot: number };

/** Stagger between pieces moving in the same phase, per level, in ms. */
const STAGGER = { puzzle: 0, grid: 160, quadrant: 70, subgrid: 22 };
/** Pause after a phase's last move starts before the next phase begins. */
const PHASE_GAP = 700;

/** Every piece's required flip and turn, biggest pieces first, working from the solved orientations. */
const planReveal = (s: UltrarubigramStoreType): { rootDelta: number; levels: Move[][] } | null => {
  const solved = solvePuzzle(s.grids, s.solution);
  if (!solved) return null;

  const levels: Move[][] = [[], [], []];
  const rootEl = solved.rootEl;
  let rootDelta = ((rootEl * 90 - s.rot) % 360 + 360) % 360;
  if (rootDelta === 270) rootDelta = -90;

  s.grids.forEach((grid, g) => {
    const gridNode = solved.grids[g];
    levels[0].push({ path: { grid: g }, ...movesTo(grid, compose(inverse(rootEl), gridNode.el)) });

    grid.quadrants.forEach((quadrant, q) => {
      const quadrantNode = gridNode.children[q];
      levels[1].push({
        path: { grid: g, quadrant: q },
        ...movesTo(quadrant, compose(inverse(gridNode.el), quadrantNode.el)),
      });

      quadrant.subgrids.forEach((subgrid, sub) => {
        const subgridNode = quadrantNode.children[sub];
        levels[2].push({
          path: { grid: g, quadrant: q, subgrid: sub },
          ...movesTo(subgrid, compose(inverse(quadrantNode.el), subgridNode.el)),
        });
      });
    });
  });

  return { rootDelta, levels };
};

const playRevealAnimation = () => {
  const plan = planReveal(UltrarubigramStore.getRawState());
  if (!plan) {
    // Can't happen for a scrambled puzzle, but never leave the player stuck mid-reveal.
    UltrarubigramStore.update(s => { s.isRevealing = false; });
    return;
  }

  const timeline: { at: number; run: () => void }[] = [];
  let at = 300;

  if (plan.rootDelta !== 0) {
    timeline.push({ at, run: () => UltrarubigramStore.update(s => { s.rot += plan.rootDelta; }) });
    at += PHASE_GAP;
  }

  const staggers = [STAGGER.grid, STAGGER.quadrant, STAGGER.subgrid];
  plan.levels.forEach((moves, level) => {
    const flips = moves.filter(m => m.toggle !== null);
    const turns = moves.filter(m => m.deltaRot !== 0);

    for (const phase of [flips, turns]) {
      if (phase.length === 0) continue;
      phase.forEach((move, i) => {
        timeline.push({
          at: at + i * staggers[level],
          run: () => UltrarubigramStore.update(s => {
            if (move.toggle && phase === flips) toggleFlip(s, move.path, move.toggle);
            else {
              // A reveal turn is applied directly: the planned delta is already in local terms.
              pieceAt(s, move.path).rot += move.deltaRot;
              beginMove(s, move.path, 'turn');
            }
          }),
        });
      });
      at += (phase.length - 1) * staggers[level] + PHASE_GAP;
    }
  });

  timeline.push({ at, run: () => UltrarubigramStore.update(s => { s.isRevealing = false; }) });

  for (const step of timeline) setTimeout(step.run, step.at);
};

// ---------------------------------------------------------------------------
// Victory
// ---------------------------------------------------------------------------

/** After the final move's own animation; the wave starts from the top-left corner then. */
const WAVE_START = 500;
/** Milliseconds between a 3x3's spin and the next diagonal's. */
const WAVE_STEP = 60;
/** The whole puzzle's spin and the swell that lands it; matches .celebrating in the stylesheet. */
const VICTORY_SPIN_DURATION = 1700;
const STATS_DIALOG_DELAY_AFTER_VICTORY = 400;

/** Where a 3x3 sits on screen: row and column of 3x3 blocks, 0..7 from the top-left. */
const screenBlock = (s: UltrarubigramStoreType, path: Required<PiecePath>): [number, number] => {
  const root = rotationElement(s.rot);
  const grid = s.grids[path.grid];
  const gridEl = compose(root, orientationElement(grid));
  const [gx, gy] = apply(root, ...childOffset(path.grid, GRID_HALF_SIZE));
  const quadrant = grid.quadrants[path.quadrant];
  const quadrantEl = compose(gridEl, orientationElement(quadrant));
  const [qx, qy] = apply(gridEl, ...childOffset(path.quadrant, QUADRANT_HALF_SIZE));
  const [sx, sy] = apply(quadrantEl, ...childOffset(path.subgrid, SUBGRID_HALF_SIZE));
  // the 3x3's centre in half-cells from the puzzle's centre; blocks are 6 half-cells apart
  const x = gx + qx + sx;
  const y = gy + qy + sy;
  return [(y + PUZZLE_HALF_SIZE - 3) / 6, (x + PUZZLE_HALF_SIZE - 3) / 6];
};

/** A full clockwise turn on screen, whatever the piece's parents have done to it. */
const spinOnScreen = (s: UltrarubigramStoreType, path: PiecePath) => {
  const piece = pieceAt(s, path);
  const mirrored = isMirrored(parentElement(s, path)) !== (piece.flipH !== piece.flipV);
  piece.rot += 360 * (mirrored ? -1 : 1);
  beginMove(s, path, 'turn');
};

/**
 * Spins every 3x3 in a wave from the top-left corner to the bottom-right, then spins the whole
 * puzzle once while the board swells and settles. Nothing ends up anywhere new.
 */
const playVictoryAnimation = (onDone: () => void) => {
  // planned once the final move has landed, so the wave follows where the 3x3s are on screen
  setTimeout(() => {
    const s = UltrarubigramStore.getRawState();
    const timeline: { at: number; run: () => void }[] = [];
    let lastSpin = 0;

    s.grids.forEach((grid, g) => grid.quadrants.forEach((quadrant, q) => quadrant.subgrids.forEach((_, sub) => {
      const path = { grid: g, quadrant: q, subgrid: sub };
      const [row, col] = screenBlock(s, path);
      const at = (row + col) * WAVE_STEP;
      lastSpin = Math.max(lastSpin, at);
      timeline.push({ at, run: () => UltrarubigramStore.update(_s => spinOnScreen(_s, path)) });
    })));

    // the whole puzzle goes as the last 3x3 is finishing
    const spinAt = lastSpin + FLIP_DURATION - 150;
    timeline.push({
      at: spinAt,
      run: () => UltrarubigramStore.update(_s => {
        _s.isCelebrating = true;
        _s.rot += 360;
      }),
    });
    timeline.push({
      at: spinAt + VICTORY_SPIN_DURATION,
      run: () => {
        UltrarubigramStore.update(_s => { _s.isCelebrating = false; });
        onDone();
      },
    });

    for (const step of timeline) setTimeout(step.run, step.at);
  }, WAVE_START);
};

/** Everything a static, solved rendering needs: the solution cut into identity-oriented pieces. */
export const solvedGrids = (solution: string[][]): GridState[] => {
  const grids = makeEmptyGrids();
  fillContent(grids, solution);
  return grids;
};

export type { GridState, QuadrantState, SubgridState };
